import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, ListTodo, Check } from "lucide-react";

const TODO_COLORS = [
  "bg-rose-50 border-rose-200 hover:border-rose-300",
  "bg-amber-50 border-amber-200 hover:border-amber-300",
  "bg-emerald-50 border-emerald-200 hover:border-emerald-300",
  "bg-sky-50 border-sky-200 hover:border-sky-300",
  "bg-violet-50 border-violet-200 hover:border-violet-300",
  "bg-fuchsia-50 border-fuchsia-200 hover:border-fuchsia-300",
];

function colorFor(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return TODO_COLORS[h % TODO_COLORS.length];
}

export default function Todo() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/personal-todos");
      setTodos(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addTodo = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post("/personal-todos", { text: text.trim() });
      setTodos((prev) => [...prev, data]);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleDone = async (t) => {
    try {
      const { data } = await api.patch(`/personal-todos/${t.id}`, { done: !t.done });
      setTodos((prev) => prev.map((x) => (x.id === t.id ? data : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveEdit = async (t) => {
    const newText = editing[t.id]?.trim();
    if (!newText || newText === t.text) {
      setEditing((prev) => ({ ...prev, [t.id]: undefined }));
      return;
    }
    try {
      const { data } = await api.patch(`/personal-todos/${t.id}`, { text: newText });
      setTodos((prev) => prev.map((x) => (x.id === t.id ? data : x)));
      setEditing((prev) => ({ ...prev, [t.id]: undefined }));
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeTodo = async (t) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/personal-todos/${t.id}`);
      setTodos((prev) => prev.filter((x) => x.id !== t.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openItems = todos.filter((t) => !t.done);
  const doneItems = todos.filter((t) => t.done);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-rose-50/30">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 shadow-sm text-xs text-slate-600 mb-3">
            <ListTodo size={12} className="text-fuchsia-600" /> Private to you
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            My personal to-do list
          </h1>
          <p className="text-slate-500 mt-2">Notes, ideas, reminders — only you can see this.</p>
        </div>

        <Card className="rounded-xl border-slate-200 shadow-sm p-4 mb-6 bg-white">
          <form onSubmit={addTodo} className="flex gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a new item and press Enter…"
              className="flex-1 h-11"
              data-testid="todo-input"
              autoFocus
            />
            <Button type="submit" disabled={!text.trim()} className="h-11" data-testid="todo-add-btn">
              <Plus size={16} className="mr-1.5" /> Add
            </Button>
          </form>
        </Card>

        {loading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : todos.length === 0 ? (
          <Card className="p-10 rounded-xl border-dashed border-slate-300 shadow-none text-center bg-white/60">
            <ListTodo className="mx-auto text-slate-300" size={40} />
            <p className="text-slate-500 mt-3 text-sm">Nothing here yet — start with a small task.</p>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="todo-list">
            {openItems.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold px-1">
                  Open ({openItems.length})
                </div>
                {openItems.map((t) => (
                  <TodoRow
                    key={t.id}
                    t={t}
                    tint={colorFor(t.id)}
                    isEditing={editing[t.id] !== undefined}
                    editValue={editing[t.id] ?? t.text}
                    onEdit={() => setEditing((prev) => ({ ...prev, [t.id]: t.text }))}
                    onEditChange={(v) => setEditing((prev) => ({ ...prev, [t.id]: v }))}
                    onSave={() => saveEdit(t)}
                    onCancelEdit={() => setEditing((prev) => ({ ...prev, [t.id]: undefined }))}
                    onToggle={() => toggleDone(t)}
                    onRemove={() => removeTodo(t)}
                  />
                ))}
              </div>
            )}
            {doneItems.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold px-1 flex items-center gap-1.5">
                  <Check size={11} /> Completed ({doneItems.length})
                </div>
                {doneItems.map((t) => (
                  <TodoRow
                    key={t.id}
                    t={t}
                    tint="bg-slate-100 border-slate-200"
                    isEditing={editing[t.id] !== undefined}
                    editValue={editing[t.id] ?? t.text}
                    onEdit={() => setEditing((prev) => ({ ...prev, [t.id]: t.text }))}
                    onEditChange={(v) => setEditing((prev) => ({ ...prev, [t.id]: v }))}
                    onSave={() => saveEdit(t)}
                    onCancelEdit={() => setEditing((prev) => ({ ...prev, [t.id]: undefined }))}
                    onToggle={() => toggleDone(t)}
                    onRemove={() => removeTodo(t)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function TodoRow({ t, tint, isEditing, editValue, onEdit, onEditChange, onSave, onCancelEdit, onToggle, onRemove }) {
  return (
    <div className={`group p-3 rounded-lg border-2 shadow-sm transition-all ${tint} ${t.done ? "opacity-70" : ""}`} data-testid={`todo-row-${t.id}`}>
      <div className="flex items-start gap-3">
        <Checkbox
          checked={t.done}
          onCheckedChange={onToggle}
          className="mt-1"
          data-testid={`todo-check-${t.id}`}
        />
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex gap-2">
              <Input
                value={editValue}
                onChange={(e) => onEditChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSave();
                  if (e.key === "Escape") onCancelEdit();
                }}
                autoFocus
                data-testid={`todo-edit-${t.id}`}
                className="h-9"
              />
              <Button size="sm" onClick={onSave} data-testid={`todo-save-${t.id}`}>Save</Button>
              <Button size="sm" variant="outline" onClick={onCancelEdit}>Cancel</Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onEdit}
              className={`text-left text-sm text-slate-900 leading-relaxed w-full ${t.done ? "line-through" : ""}`}
              data-testid={`todo-text-${t.id}`}
            >
              {t.text}
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={onRemove}
          data-testid={`todo-delete-${t.id}`}
          aria-label="Delete"
        >
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  );
}
